/**
 * PolicyCloneModal - Clone a Template Policy
 *
 * Modal for cloning a template policy with a new name and optional modifications.
 */

import { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { apiClient } from '@/services/apiClient';
import type { Policy } from '@/types/policy';
import { getErrorMessage } from '@/utils/errorHandling';

interface PolicyCloneModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (clonedPolicy?: Policy) => void;
  policy: Policy | null;
}

export default function PolicyCloneModal({
  show,
  onHide,
  onSuccess,
  policy,
}: PolicyCloneModalProps) {
  const { t } = useTranslation();

  const [newName, setNewName] = useState('');
  const [description, setDescription] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (show && policy) {
      setNewName(`${policy.name} (Copy)`);
      setDescription(policy.description || '');
      setError(null);
    }
  }, [show, policy]);

  const handleClone = async () => {
    if (!policy) return;

    if (!newName.trim()) {
      setError(t('admin.policyManagement.nameRequired', 'Policy name is required'));
      return;
    }

    if (newName.length > 255) {
      setError(t('admin.policyManagement.nameTooLong', 'Policy name must be 255 characters or less'));
      return;
    }

    setIsCloning(true);
    setError(null);

    try {
      const response = await apiClient.clonePolicy(policy.id, {
        new_name: newName,
        description: description || null,
      });

      if (response.error) {
        toast.error(response.error);
        setError(response.error);
        setIsCloning(false);
        return;
      }

      toast.success(
        t('admin.policyManagement.policyCloned', 'Policy cloned successfully')
      );

      setIsCloning(false);
      onSuccess(response.data as Policy | undefined);
      onHide();
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Failed to clone policy';
      toast.error(errorMessage);
      setError(errorMessage);
      setIsCloning(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('admin.policyManagement.clonePolicy', 'Clone Policy Template')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {policy && (
          <Alert variant="info">
            <strong>{t('admin.policyManagement.sourceTemplate', 'Source Template')}:</strong>{' '}
            {policy.name}
            {policy.description && (
              <div className="small mt-1 text-muted">{policy.description}</div>
            )}
            <div className="mt-2 small">
              <strong>{t('admin.policyManagement.statements', 'Statements')}:</strong>{' '}
              {policy.policy_document.Statement.length}
            </div>
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>
            {t('admin.policyManagement.newPolicyName', 'New Policy Name')}{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t(
              'admin.policyManagement.newPolicyNamePlaceholder',
              'Enter name for the cloned policy'
            )}
            maxLength={255}
            required
          />
          <Form.Text className="text-muted">
            {t(
              'admin.policyManagement.newPolicyNameHelp',
              'Unique name for the cloned policy (max 255 characters)'
            )}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            {t('admin.policyManagement.description', 'Description')}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t(
              'admin.policyManagement.descriptionPlaceholder',
              'Optional description for the cloned policy'
            )}
          />
        </Form.Group>

        <Alert variant="info">
          <small>
            {t(
              'admin.policyManagement.cloneHelp',
              'The cloned policy will have the same policy document as the template. You can edit it after cloning.'
            )}
          </small>
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isCloning}>
          {t('admin.policyManagement.cancel', 'Cancel')}
        </Button>
        <Button variant="primary" onClick={handleClone} disabled={isCloning}>
          {isCloning
            ? t('admin.policyManagement.cloning', 'Cloning...')
            : t('admin.policyManagement.clone', 'Clone Policy')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
